import { useEffect, useRef, useState } from 'react';

//hooks
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

//components
import {
  Section,
  SectionWrap,
  Kicker,
  KickerDot,
  SectionTitle,
} from '@/containers-ai/SectionHeading';

//styles
import {
  Theater,
  Tabs,
  Tab,
  TabNumber,
  TabTitle,
  TabSubtitle,
  StageBox,
  StageHead,
  StageHeadTitle,
  StageHeadTag,
  StageStatus,
  StageLog,
  LogRow,
  LogTime,
  LogText,
  StageSummary,
  StageSummaryLabel,
  StageFoot,
} from './ScenarioTheater.styles';

const LINE_INTERVAL = 950;
const AUTO_ADVANCE_INTERVAL = 9500;

const SCENARIOS = [
  {
    tab: 'Document collection & filing',
    td: 'Docs missing on delivered loads',
    agent: 'Docs Agent',
    nite: '3 delivered loads are missing signed BOLs and a rate con.',
    ref: 'Documents · #LS-48180',
    lines: [
      {
        time: '15:12:04',
        text: '3 delivered loads flagged — documents missing in your TMS.',
      },
      {
        time: '15:12:06',
        text: 'Emailed and called each carrier for the signed BOL and rate con.',
        highlight: true,
      },
      {
        time: '15:12:40',
        text: '2 back — read, validated against the shipment, filed.',
        highlight: true,
      },
      {
        time: '15:13:20',
        text: '1 carrier sent the wrong file — flagged and re-requested.',
        highlight: true,
      },
      {
        time: '15:14:02',
        text: (
          <>
            <b>All 3 filed to your TMS.</b> Every doc matched to its load.
          </>
        ),
        highlight: true,
      },
    ],
    summary: (
      <>
        A stack of delivered loads was missing paperwork. Loadsmart AI chased
        every carrier by email and phone, read and validated each document,
        caught a wrong file, and filed all three to your system of record
        &mdash; <b>your team chased none of them.</b>
      </>
    ),
  },
  {
    tab: 'Tracking & status updates',
    td: "A load's gone quiet",
    agent: 'Tracking Agent',
    nite: "A customer's asking where load #LS-51204 is — and it's gone quiet.",
    ref: 'Tracking · #LS-51204',
    lines: [
      {
        time: '09:14:10',
        text: (
          <>
            No tracking ping on <b>#LS-51204</b> for 4 hours.
          </>
        ),
      },
      {
        time: '09:14:12',
        text: "Reached the carrier — truck's rolling, 40 min behind.",
        highlight: true,
      },
      {
        time: '09:14:40',
        text: 'ETA recalculated and written back to your TMS.',
        highlight: true,
      },
      {
        time: '09:14:42',
        text: 'Still inside the delivery window — nothing needed from you.',
        highlight: true,
      },
      {
        time: '09:14:45',
        text: (
          <>
            <b>Clean status pushed to the customer</b> before they asked
            again.
          </>
        ),
        highlight: true,
      },
    ],
    summary: (
      <>
        A load stopped reporting and the customer was getting anxious.
        Loadsmart AI chased the carrier, confirmed the truck was moving,
        updated the ETA, and sent the customer a clean status &mdash;{' '}
        <b>the check calls your team never had to make.</b>
      </>
    ),
  },
  {
    tab: 'Tender failures',
    td: 'A tender just fell through',
    agent: 'Procurement Agent',
    nite: 'A carrier just rejected the tender on MEM → ORD, picking up tomorrow.',
    ref: 'Procurement · #LS-49010',
    lines: [
      {
        time: '17:02:01',
        text: 'Tender rejected on MEM → ORD — pickup tomorrow 6am.',
      },
      {
        time: '17:02:03',
        text: 'Re-tendered to the next carrier on your routing guide.',
        highlight: true,
      },
      {
        time: '17:02:44',
        text: 'No response in your 40-min window — moved to the third.',
        highlight: true,
      },
      {
        time: '17:03:10',
        text: 'Covered at contract. Rate con out, dock appointment held.',
        highlight: true,
      },
      {
        time: '17:03:12',
        text: <b>Re-covered before the miss reached your team.</b>,
        highlight: true,
      },
    ],
    summary: (
      <>
        A carrier fell through on a next-day load. Loadsmart AI worked down
        your routing guide, re-tendered until it re-covered at contract, and
        kept the dock appointment &mdash;{' '}
        <b>a rejected tender never became a scramble on your desk.</b>
      </>
    ),
  },
  {
    tab: 'Proactive load audits',
    td: "A load's at risk before pickup",
    agent: 'Audit Agent',
    nite: "Pickups start in 2 hours — and something's off on three loads.",
    ref: 'Audit · AM pickups',
    lines: [
      {
        time: '05:58:00',
        text: 'Pre-pickup audit on 38 loads picking up this morning.',
      },
      {
        time: '05:58:20',
        text: (
          <>
            <b>#LS-50733</b>: tender says reefer, the order needs a dry van.
          </>
        ),
        highlight: true,
      },
      {
        time: '05:58:22',
        text: 'Carrier confirmed the mix-up — corrected before dispatch.',
        highlight: true,
      },
      {
        time: '05:58:40',
        text: '2 more flagged: appointment missing, instructions stale.',
        highlight: true,
      },
      {
        time: '05:59:05',
        text: (
          <>
            <b>All 3 fixed before pickup.</b> No day-of surprises.
          </>
        ),
        highlight: true,
      },
    ],
    summary: (
      <>
        Before the trucks rolled, Loadsmart AI audited the morning&rsquo;s
        loads and caught a wrong equipment type, a missing appointment, and
        stale instructions &mdash; fixing all three ahead of pickup so{' '}
        <b>
          none turned into detention, a rejection, or a day-of fire drill.
        </b>
      </>
    ),
  },
  {
    tab: 'Claims processing',
    td: 'Concealed damage on delivery',
    agent: 'Claims Agent',
    nite: 'Receiver just reported concealed damage on delivered load #LS-47788.',
    ref: 'Claims · #LS-47788',
    lines: [
      {
        time: '10:20:00',
        text: (
          <>
            Concealed-damage claim opened on <b>#LS-47788</b>.
          </>
        ),
      },
      {
        time: '10:20:15',
        text: 'Pulled the BOL, POD, photos and delivery notes automatically.',
        highlight: true,
      },
      {
        time: '10:20:40',
        text: 'Validated against carrier liability and the commodity value.',
        highlight: true,
      },
      {
        time: '10:21:10',
        text: 'Drafted the claim packet — ready for your one-click approval.',
        highlight: true,
      },
      {
        time: '10:21:12',
        text: (
          <>
            <b>Filed the moment you approve.</b> Nothing left on the table.
          </>
        ),
        highlight: true,
      },
    ],
    summary: (
      <>
        Concealed damage usually goes unclaimed because filing takes too
        long. Loadsmart AI gathered every document, validated the claim
        against carrier liability, and drafted the packet for your approval
        &mdash; <b>turning a write-off into recovered dollars.</b>
      </>
    ),
  },
  {
    tab: 'Scheduling & re-scheduling',
    td: 'A pickup just slipped',
    agent: 'Scheduling Agent',
    nite: "The 8am pickup on #LS-52001 just slipped — carrier's running late.",
    ref: 'Scheduling · #LS-52001',
    lines: [
      {
        time: '07:35:00',
        text: 'Carrier flagged the 8am pickup will slip ~3 hours.',
      },
      {
        time: '07:35:20',
        text: 'Checked dock availability — rebooked the slot for 11:15am.',
        highlight: true,
      },
      {
        time: '07:35:40',
        text: 'Delivery appointment shifted to hold the same service window.',
        highlight: true,
      },
      {
        time: '07:35:55',
        text: 'Dock, receiver and customer notified of the new times.',
        highlight: true,
      },
      {
        time: '07:36:00',
        text: (
          <>
            <b>Re-scheduled and everyone&rsquo;s aligned</b> &mdash; no missed
            dock.
          </>
        ),
        highlight: true,
      },
    ],
    summary: (
      <>
        A late carrier threw off a booked pickup. Loadsmart AI found a new
        dock slot, shifted the delivery appointment to protect the service
        window, and notified the dock, receiver and customer &mdash;{' '}
        <b>
          a slipped pickup didn&rsquo;t cascade into a missed appointment.
        </b>
      </>
    ),
  },
];

export function ScenarioTheater() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleLines, setVisibleLines] = useState(0);
  const [done, setDone] = useState(false);
  const lineTimerRef = useRef(null);
  const autoAdvanceRef = useRef(null);
  const userInteractedRef = useRef(false);

  const active = SCENARIOS[activeIndex];

  function selectScenario(index) {
    userInteractedRef.current = true;
    if (autoAdvanceRef.current) {
      clearInterval(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
    setActiveIndex(index);
  }

  useEffect(() => {
    if (prefersReducedMotion || userInteractedRef.current) return undefined;

    autoAdvanceRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % SCENARIOS.length);
    }, AUTO_ADVANCE_INTERVAL);

    return () => clearInterval(autoAdvanceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (lineTimerRef.current) clearInterval(lineTimerRef.current);
    setDone(false);

    if (prefersReducedMotion) {
      setVisibleLines(active.lines.length);
      setDone(true);
      return undefined;
    }

    setVisibleLines(0);
    let li = 0;
    lineTimerRef.current = setInterval(() => {
      if (li < active.lines.length) {
        li += 1;
        setVisibleLines(li);
      } else {
        clearInterval(lineTimerRef.current);
        lineTimerRef.current = null;
        setDone(true);
      }
    }, LINE_INTERVAL);

    return () => clearInterval(lineTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, prefersReducedMotion]);

  return (
    <Section id="scenario-theater">
      <SectionWrap>
        <Kicker>
          <KickerDot />
          Watch it run your freight
        </Kicker>
        <SectionTitle>Pick the fire. Watch it go out.</SectionTitle>

        <Theater>
          <Tabs role="tablist">
            {SCENARIOS.map((scenario, index) => (
              <Tab
                key={scenario.tab}
                type="button"
                role="tab"
                aria-selected={index === activeIndex}
                $active={index === activeIndex}
                onClick={() => selectScenario(index)}
              >
                <TabNumber $active={index === activeIndex}>
                  {String(index + 1).padStart(2, '0')}
                </TabNumber>
                <div>
                  <TabTitle $active={index === activeIndex}>
                    {scenario.tab}
                  </TabTitle>
                  <TabSubtitle>{scenario.td}</TabSubtitle>
                </div>
              </Tab>
            ))}
          </Tabs>

          <StageBox>
            <StageHead>
              <div>
                <StageHeadTitle>{active.nite}</StageHeadTitle>
                <StageHeadTag>{active.ref}</StageHeadTag>
              </div>
              <StageStatus $done={done}>
                {done ? 'Resolved' : 'Working'}
              </StageStatus>
            </StageHead>
            <StageLog>
              {active.lines.map((line, index) => (
                <LogRow
                  key={index}
                  $highlight={line.highlight}
                  $show={index < visibleLines}
                >
                  <LogTime>{line.time}</LogTime>
                  <LogText $highlight={line.highlight}>{line.text}</LogText>
                </LogRow>
              ))}
            </StageLog>
            <StageSummary $show={done}>
              <StageSummaryLabel>What just happened</StageSummaryLabel>
              {active.summary}
            </StageSummary>
            <StageFoot>
              <span>Loadsmart AI · scripted session</span>
              <span>{active.agent}</span>
            </StageFoot>
          </StageBox>
        </Theater>
      </SectionWrap>
    </Section>
  );
}
